/**
 * Car Chat Engine - AI Response System
 * Intelligent intent detection and context-aware response generation
 * Enhanced with Knowledge Graph RAG integration
 */

import { chatPatterns } from '../data/chatPatterns';
import { queryKnowledgeGraph, shouldUseKnowledgeGraph, formatCitations } from './knowledgeGraphAPI';

export class CarChatEngine {
  constructor(vehicleData, vehicleType = 'nexon') {
    this.vehicleData = vehicleData;
    this.vehicleType = vehicleType;
    this.conversationHistory = [];
    this.lastIntent = null;
  }

  /**
   * Update vehicle data for real-time responses
   */
  updateVehicleData(vehicleData) {
    this.vehicleData = vehicleData;
  }

  /**
   * Set vehicle type (nexon = light commercial EV, nano = city car)
   */
  setVehicleType(vehicleType) {
    this.vehicleType = vehicleType;
    console.log(`[Chat Engine] Vehicle type changed to: ${vehicleType}`);
  }

  /**
   * Detect intent from user message
   */
  detectIntent(message) {
    const normalizedMessage = message.toLowerCase().trim();

    // Check each pattern category
    for (const [intent, config] of Object.entries(chatPatterns)) {
      if (config.patterns) {
        for (const pattern of config.patterns) {
          if (pattern.test(normalizedMessage)) {
            return intent;
          }
        }
      }
    }

    return 'unknown';
  }

  /**
   * Generate response based on intent
   */
  generateResponse(intent, userMessage) {
    const pattern = chatPatterns[intent];

    if (!pattern) {
      return this.getRandomResponse(chatPatterns.unknown.responses);
    }

    // If pattern has generateResponse function, use it with vehicle data
    if (pattern.generateResponse) {
      try {
        return pattern.generateResponse(this.vehicleData);
      } catch (error) {
        console.error('Error generating response:', error);
        return "I'm having trouble accessing vehicle data. Please try again.";
      }
    }

    // Otherwise, return a random static response
    if (pattern.responses) {
      return this.getRandomResponse(pattern.responses);
    }

    return "I'm not sure how to respond to that.";
  }

  /**
   * Get random response from array
   */
  getRandomResponse(responses) {
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Process user message and return AI response
   * Enhanced with Knowledge Graph RAG integration
   */
  async chat(userMessage) {
    // Add user message to history
    this.conversationHistory.push({
      role: 'user',
      message: userMessage,
      timestamp: new Date()
    });

    // --- Routing Logic ---
    // 1. Simple conversational messages (hi, thanks, help) → handle locally (instant)
    // 2. All technical/vehicle questions → Knowledge Graph API + Ollama LLM
    // 3. If KG fails → fall back to local dummy data patterns
    const localOnlyIntents = ['greeting', 'thanks', 'help'];
    const detectedIntent = this.detectIntent(userMessage);
    const isLocalOnly = localOnlyIntents.includes(detectedIntent);

    // Send to Knowledge Graph unless it's a simple conversational message
    const useKG = !isLocalOnly;
    console.log('[CarChatEngine] Question:', userMessage);
    console.log('[CarChatEngine] Detected intent:', detectedIntent);
    console.log('[CarChatEngine] Use Knowledge Graph?', useKG);

    if (useKG) {
      try {
        console.log(`[CarChatEngine] Querying Knowledge Graph API for ${this.vehicleType}...`);

        // Prepare conversation history (last 15 messages) for context
        const historyForAPI = this.conversationHistory.slice(-15).map(msg => ({
          role: msg.role,
          message: msg.message,
          timestamp: msg.timestamp?.toISOString?.() || new Date().toISOString()
        }));

        console.log(`[CarChatEngine] Sending ${historyForAPI.length} messages for context`);

        // Query Knowledge Graph API with vehicle type and conversation history
        const kgResponse = await queryKnowledgeGraph(
          userMessage,
          this.vehicleType,
          false,
          historyForAPI  // Pass last 15 messages
        );
        console.log('[CarChatEngine] KG Response:', kgResponse);

        if (kgResponse.success && kgResponse.answer) {
          // Format citations
          const citations = formatCitations(kgResponse.citations);

          // Extract multimodal content
          const images = kgResponse.images || [];
          const tables = kgResponse.tables || [];
          const hasVisualContent = kgResponse.hasVisualContent || false;

          console.log('[CarChatEngine] 🎨 MULTIMODAL CONTENT IN RESPONSE:');
          console.log('[CarChatEngine] 📷 Images:', images.length);
          console.log('[CarChatEngine] 📊 Tables:', tables.length);
          console.log('[CarChatEngine] ✨ Has Visual Content:', hasVisualContent);

          if (images.length > 0) {
            console.log('[CarChatEngine] 🖼️ Image details:');
            images.forEach((img, idx) => {
              console.log(`[CarChatEngine]   Image ${idx + 1}:`, {
                page: img.page,
                file_path: img.file_path,
                description: img.description
              });
            });
          }

          if (tables.length > 0) {
            console.log('[CarChatEngine] 📋 Table details:');
            tables.forEach((table, idx) => {
              console.log(`[CarChatEngine]   Table ${idx + 1}:`, {
                page: table.page,
                caption: table.caption,
                rows: table.dimensions?.rows,
                cols: table.dimensions?.cols
              });
            });
          }

          // Add AI response to history
          this.conversationHistory.push({
            role: 'ai',
            message: kgResponse.answer,
            intent: kgResponse.intent || 'knowledge_graph',
            timestamp: new Date(),
            confidence: kgResponse.confidence,
            citations: citations,
            reasoningPath: kgResponse.reasoningPath,
            retrievalDetails: kgResponse.retrievalDetails,  // NEW: Hybrid RAG details
            isHybrid: kgResponse.isHybrid,  // NEW: Flag for hybrid mode
            // NEW: Multimodal content
            images: images,
            tables: tables,
            hasVisualContent: hasVisualContent
          });

          return {
            message: kgResponse.answer,
            intent: kgResponse.intent || 'knowledge_graph',
            timestamp: new Date(),
            confidence: kgResponse.confidence,
            citations: citations,
            reasoningPath: kgResponse.reasoningPath,
            retrievalDetails: kgResponse.retrievalDetails,  // NEW
            isHybrid: kgResponse.isHybrid,  // NEW
            // NEW: Multimodal content
            images: images,
            tables: tables,
            hasVisualContent: hasVisualContent
          };
        }

        // KG API responded but returned no answer — return error, do not fall back to pattern matching
        const noAnswerMsg = "I couldn't find a relevant answer in the knowledge base for that question. Please try rephrasing or ask about a specific component or system.";
        this.conversationHistory.push({ role: 'ai', message: noAnswerMsg, intent: 'unknown', timestamp: new Date() });
        await new Promise(resolve => setTimeout(resolve, 300));
        return { message: noAnswerMsg, intent: 'unknown', timestamp: new Date() };

      } catch (error) {
        console.error('Knowledge Graph query failed:', error);
        // Backend is not reachable — return a clear error instead of misleading pattern-matched response
        const offlineMsg = "I'm unable to reach the knowledge base (backend not running at http://localhost:8000). Please start the API server and try again.";
        this.conversationHistory.push({ role: 'ai', message: offlineMsg, intent: 'error', timestamp: new Date() });
        await new Promise(resolve => setTimeout(resolve, 300));
        return { message: offlineMsg, intent: 'error', timestamp: new Date() };
      }
    }

    // Fallback: local pattern matching only for conversational queries (greeting, thanks, help)
    const intent = this.detectIntent(userMessage);
    this.lastIntent = intent;

    // Generate response
    const response = this.generateResponse(intent, userMessage);

    // Add AI response to history
    this.conversationHistory.push({
      role: 'ai',
      message: response,
      intent: intent,
      timestamp: new Date()
    });

    // Simulate async processing delay for realistic feel
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));

    return {
      message: response,
      intent: intent,
      timestamp: new Date()
    };
  }

  /**
   * Get conversation context for follow-up questions
   */
  getContext() {
    return {
      lastIntent: this.lastIntent,
      history: this.conversationHistory.slice(-5), // Last 5 messages
      vehicleState: this.getVehicleStateSummary()
    };
  }

  /**
   * Get summary of current vehicle state
   */
  getVehicleStateSummary() {
    return {
      soc: this.vehicleData?.battery?.soc || 0,
      range: typeof this.vehicleData?.battery?.range === 'object'
        ? this.vehicleData?.battery?.range?.estimated
        : this.vehicleData?.battery?.range || 0,
      hasWarnings: (this.vehicleData?.alerts || []).length > 0,
      warningCount: (this.vehicleData?.alerts || []).length,
      batteryTemp: this.vehicleData?.battery?.temperature?.pack || 0,
      motorTemp: this.vehicleData?.motor?.temperature || 0
    };
  }

  /**
   * Clear conversation history
   */
  clearHistory() {
    this.conversationHistory = [];
    this.lastIntent = null;
  }

  /**
   * Get smart suggestions based on vehicle state
   */
  getSuggestions() {
    const suggestions = [];
    const state = this.getVehicleStateSummary();

    // Low SOC suggestion
    if (state.soc < 30) {
      suggestions.push("Your battery is low. Ask me about charging options.");
    }

    // Warnings suggestion
    if (state.hasWarnings) {
      suggestions.push(`You have ${state.warningCount} warning${state.warningCount > 1 ? 's' : ''}. Ask me to explain.`);
    }

    // High temperature suggestion
    if (state.batteryTemp > 40 || state.motorTemp > 90) {
      suggestions.push("Temperatures are elevated. Ask me about cooling recommendations.");
    }

    // Low range suggestion
    if (state.range < 100) {
      suggestions.push("Low range detected. Ask about charging stations or range optimization.");
    }

    return suggestions;
  }

  /**
   * Export conversation
   */
  exportConversation() {
    const formatted = this.conversationHistory.map(entry => {
      const time = entry.timestamp.toLocaleTimeString();
      const role = entry.role === 'user' ? 'You' : 'AI Assistant';
      return `[${time}] ${role}: ${entry.message}`;
    }).join('\n\n');

    return formatted;
  }
}

export default CarChatEngine;
